import { AnyAction, createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, delay, filter, mergeMap } from 'rxjs/operators';

import { api } from '~constants/api';

import { actions as appActions } from '../app/appSlice';
import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as profileActions } from '../profile/profileSlice';
import { actions as sharedActions } from '../sharedSlice';

import {
  getSettingsRequestConfig,
  SettingsTab,
  SystemSettingsSection,
  ServerSettingsSection,
  NetworkSettingsSection,
  getSystemSettingsRequestConfig,
  getServerSettingsRequestConfig,
  getNetworkSettingsRequestConfig,
} from './settingsUtils';

import {
  NetworkSettings,
  ServerSettings,
  TechOption,
  ResponseError,
  SubToolsSettings,
  SubToolsExecuteSection,
  CoordinateSettings,
  LengthUnit,
  SystemCheckInGraceConfiguration,
  Antenna,
  FCCData,
  InstancesInfo,
  SSLRequest,
  DatabaseDump,
  ScheduledDBDumpStatus,
  MFAAuthenticationSettings,
  WebRelay,
  TcpSettings,
  HTTPIntegration,
  DatabaseDumpReplicationSettings,
  DatabaseDumpHistory,
  HydrantRadiusSettings,
  SMTPSettings,
  NotificationsTemplate,
  OrphanNotifications,
  AutoAckSettings,
  INCCLogFiles,
  AutoAckEventTypes,
  SMTPOutboundEmailTest,
  SMTPEmailTestConnection,
  PortConfiguration,
} from '~models';
import { http } from '~services';
import { boundActions } from '~store';
import { getResponseError, saveFile } from '~utils';

export interface SettingsState {
  data: {
    system: {
      'length-unit': LengthUnit | null;
      'database-dump': {
        list: DatabaseDump[] | null;
        status: ScheduledDBDumpStatus;
        progress: Required<AES.ProcessProgress>;
        sync: DatabaseDumpReplicationSettings | null;
        history: DatabaseDumpHistory[] | null;
      };
      password: number | null;
      'user-inactive-days': number | null;
       mfa: MFAAuthenticationSettings | null;
      'token-expiration': number | null;
      'show-ul-warning': boolean;
      smtp: SMTPSettings | null;
      'notification-template': NotificationsTemplate | null;
      notifications: OrphanNotifications | null;
      notificationsStatus: boolean | null;
      appLogs: INCCLogFiles[] | null;
      dbLogs: INCCLogFiles[] | null;
    },
    server: {
      server: ServerSettings | null;
      coordinate: CoordinateSettings | null;
    }
    network: {
      network: NetworkSettings | null;
      instances: InstancesInfo | null;
      tcp: TcpSettings;
      protocol: PortConfiguration | null;
    }
    'tech-options': TechOption[] | null;
    'sub-tools': SubToolsSettings | null;
    'check-in': SystemCheckInGraceConfiguration | null;
    fcc: FCCData | null;
    antennas: {
      list: Antenna[] | null;
      names: string[] | null;
      status: 'created' | null;
    };
    maintenance: {
      weeklyReportTimestamp: number;
    }
    selfMonitoring: {
      webRelay: WebRelay | null;
      isMonitored: boolean;
      highContrast: boolean;
      stormMode: boolean;
      alarmHistoryLifeTime: number;
      hydrantRadius: HydrantRadiusSettings | null;
      autoAckSettings: AutoAckSettings[] | null;
      autoAckEventTypes: AutoAckEventTypes | null;
    };
    integrations: {
      'http-integration': HTTPIntegration | null;
    }
  };
  loading: {
    tabs: { [key in Partial<SettingsTab>]: boolean };
    system: { [key in SystemSettingsSection]: boolean };
    server: { [key in ServerSettingsSection]: boolean };
    network: { [key in NetworkSettingsSection]: boolean };
    'sub-tools': { [key in SubToolsExecuteSection]: boolean };
    maintenance: {
      'ssl-created': boolean;
      'ssl-upload': boolean;
      weeklyReport: boolean;
    };
    antennas: {
      add: boolean;
      list: boolean;
      delete: boolean;
    };
    selfMonitoring: {
      webRelay: boolean;
      highContrast: boolean;
      stormMode: boolean;
      alarmHistoryLifeTime: boolean;
      hydrantRadius: boolean;
      autoAck: boolean;
    },
    update: boolean;
    import: boolean;
    sendOutbound: boolean;
  };
  status:
    'updated'
    | 'ip-changed'
    | 'ssl-created'
    | 'address-imported'
    | 'auto-supervision'
    | 'started'
    | 'instances-updated'
    | null;
  error: {
    tabs: { [key in SettingsTab]: ResponseError | null };
    selfMonitoring: {
      webRelay: ResponseError | null;
      highContrast: ResponseError | null;
      stormMode: ResponseError | null;
      alarmHistoryLifeTime: ResponseError | null;
      hydrantRadius: ResponseError | null;
      autoAck: ResponseError | null;
    },
    instances: ResponseError | null;
  };
}

export const initialState: SettingsState = {
  data: {
    system: {
      'length-unit': null,
      'database-dump': {
        list: null,
        status: 'OFF',
        progress: {
          loaded: 0,
          total: 0,
          completed: true,
        },
        sync: null,
        history: null,
      },
      password: null,
      'user-inactive-days': null,
      mfa: null,
      'token-expiration': null,
      'show-ul-warning': true,
      smtp: null,
      'notification-template': null,
      notifications: null,
      notificationsStatus: null,
      appLogs: null,
      dbLogs: null,
    },
    server: {
      server: null,
      coordinate: null,
    },
    network: {
      network: null,
      instances: null,
      tcp: {
        isIpLinksStop: false,
        isIpSubscribersStop: false,
      },
      protocol: null,
    },
    'tech-options': null,
    'sub-tools': null,
    'check-in': null,
    fcc: null,
    antennas: {
      list: null,
      names: null,
      status: null,
    },
    maintenance: {
      weeklyReportTimestamp: 0,
    },
    selfMonitoring: {
      webRelay: null,
      isMonitored: false,
      highContrast: false,
      stormMode: false,
      alarmHistoryLifeTime: 0,
      hydrantRadius: null,
      autoAckSettings: null,
      autoAckEventTypes: null,
    },
    integrations: {
      'http-integration': null,
    },
  },
  loading: {
    tabs: {
      system: false,
      server: false,
      network: false,
      'tech-options': false,
      'sub-tools': false,
      'check-in': false,
      antennas: false,
      fcc: false,
      maintenance: false,
      'self-monitoring': false,
      integrations: false,
    },
    system: {
      'length-unit': false,
      'database-dump': false,
      password: false,
      'user-inactive-days': false,
      mfa: false,
      'token-expiration': false,
      'show-ul-warning': false,
      'auto-supervision': false,
      smtp: false,
      'notification-template': false,
      notifications: false,
      'incc-logs': false,
    },
    server: {
      server: false,
      coordinate: false,
    },
    network: {
      network: false,
      instances: false,
      tcp: false,
      protocol: false,
    },
    'sub-tools': {
      'first-data': false,
      'refresh-data': false,
    },
    maintenance: {
      'ssl-created': false,
      'ssl-upload': false,
      weeklyReport: false,
    },
    antennas: {
      add: false,
      list: false,
      delete: false,
    },
    selfMonitoring: {
      webRelay: false,
      highContrast: false,
      stormMode: false,
      alarmHistoryLifeTime: false,
      hydrantRadius: false,
      autoAck: false,
    },
    update: false,
    import: false,
    sendOutbound: false,
  },
  error: {
    tabs: {
      system: null,
      server: null,
      network: null,
      'tech-options': null,
      'sub-tools': null,
      'check-in': null,
      antennas: null,
      fcc: null,
      maintenance: null,
      'self-monitoring': null,
      integrations: null,
    },
    selfMonitoring: {
      webRelay: null,
      highContrast: null,
      stormMode: null,
      alarmHistoryLifeTime: null,
      hydrantRadius: null,
      autoAck: null,
    },
    instances: null,
  },
  status: null,
};

export const { name, actions, reducer } = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Fetch settings
    fetchSettingsInit(state, { payload: { tab } }: PayloadAction<{ tab: SettingsTab }>) {
      state.loading.tabs[tab] = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
      state.data[tab] = initialState.data[tab];
    },
    fetchSettingsSuccess<T>(state, { payload: { tab, values } }: PayloadAction<{ tab: SettingsTab; values: T }>) {
      state.loading.tabs[tab] = false;
      state.data[tab] = values;
    },
    fetchSettingsFailed(state, { payload: { tab, error } }: PayloadAction<{ tab: SettingsTab; error: ResponseError }>) {
      state.loading.tabs[tab] = false;
      state.error.tabs[tab] = error;
    },

    // Update settings
    updateSettingsInit<T>(state, { payload: { tab } }: PayloadAction<{ tab: SettingsTab; values: T }>) {
      state.loading.update = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateSettingsSuccess(state) {
      state.loading.update = false;
      state.status = 'updated';
    },
    updateSettingsFailed(
      state,
      { payload: { tab, error } }: PayloadAction<{ tab: SettingsTab; error: ResponseError }>
    ) {
      state.loading.update = false;
      state.status = null;

      state.error.tabs[tab] = error;
    },

    // fetch system settings
    fetchSystemSettingsInit(state, { payload: { section } }: PayloadAction<{ section: SystemSettingsSection }>) {
      state.loading.system[section] = true;
    },
    fetchSystemSettingsSuccess<T>(
      state, { payload: { section, values } }: PayloadAction<{ section: SystemSettingsSection; values: T }>
    ) {
      state.loading.system[section] = false;
      state.data.system[section] = values;
    },
    fetchSystemSettingsFailed(
      state, { payload: { section, error } }: PayloadAction<{ section: SystemSettingsSection; error: ResponseError }>
    ) {
      state.loading.system[section] = false;
      state.error.tabs.system = error;
    },

    // update system settings
    updateSystemSettingsInit<T>(
      state, { payload: { section } }: PayloadAction<{ section: SystemSettingsSection; values: T }>
    ) {
      state.loading.update = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateSystemSettingsSuccess(state) {
      state.loading.update = false;
      state.status = 'updated';
    },
    updateSystemSettingsFailed(
      state,
      { payload: { error } }: PayloadAction<{ error: ResponseError }>
    ) {
      state.loading.update = false;
      state.status = null;
      state.error.tabs.system = error;
    },

    // fetch server settings
    fetchServerSettingsInit(state, { payload: { section } }: PayloadAction<{ section: ServerSettingsSection }>) {
      state.loading.server[section] = true;
    },
    fetchServerSettingsSuccess<T>(
      state, { payload: { section, values } }: PayloadAction<{ section: ServerSettingsSection; values: T }>
    ) {
      state.loading.server[section] = false;
      state.data.server[section] = values;
    },
    fetchServerSettingsFailed(
      state, { payload: { section, error } }: PayloadAction<{ section: ServerSettingsSection; error: ResponseError }>
    ) {
      state.loading.server[section] = false;
      state.error.tabs.server = error;
    },

    // update server settings
    updateServerSettingsInit<T>(
      state, { payload: { section } }: PayloadAction<{ section: ServerSettingsSection; values: T }>
    ) {
      state.loading.server[section] = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateServerSettingsSuccess(state, { payload: { section } }: PayloadAction<{ section: ServerSettingsSection }>) {
      state.loading.server[section] = false;
      state.status = 'updated';
    },
    updateServerSettingsFailed(
      state,
      { payload: { error, section } }: PayloadAction<{ error: ResponseError, section: ServerSettingsSection }>
    ) {
      state.loading.server[section] = false;
      state.status = null;
      state.error.tabs.server = error;
    },

    // fetch network settings
    fetchNetworkSettingsInit(state, { payload: { section } }: PayloadAction<{ section: NetworkSettingsSection }>) {
      state.loading.network[section] = true;
    },
    fetchNetworkSettingsSuccess<T>(
      state, { payload: { section, values } }: PayloadAction<{ section: NetworkSettingsSection; values: T }>
    ) {
      state.loading.network[section] = false;
      state.data.network[section] = values;
    },
    fetchNetworkSettingsFailed(
      state, { payload: { section, error } }: PayloadAction<{ section: NetworkSettingsSection; error: ResponseError }>
    ) {
      state.loading.network[section] = false;
      state.error.tabs.network = error;
    },

    // Update network settings
    updateNetworkSettingsInit<T>(state, { payload }: PayloadAction<{ values: T }>) {
      state.loading.update = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateNetworkSettingsSuccess<T>(state, { payload }: PayloadAction<{ ipChanged: boolean; values: T }>) {
      state.data.network = payload?.values;
      if (payload?.ipChanged) {
        state.status = 'ip-changed';
      } else {
        state.status = 'updated';
      }
      state.loading.update = false;
    },
    updateNetworkSettingsFailed(state, { payload: { error } }: PayloadAction<{ error: ResponseError }>) {
      state.loading.update = false;
      state.error.tabs.network = error;
    },

    // update Integration settings
    updateIntegrationSettingsInit<T>(
      state, { payload: { tab } }: PayloadAction<{ tab: SettingsTab; values: T }>
    ) {
      state.loading.update = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateIntegrationSettingsSuccess(state) {
      state.loading.update = false;
      state.status = 'updated';
    },
    updateIntegrationSettingsFailed(
      state,
      { payload: { tab, error } }: PayloadAction<{ tab: SettingsTab; error: ResponseError }>
    ) {
      state.loading.update = false;
      state.status = null;
      state.error.tabs[tab] = error;
    },

    // system: import CSV file
    importUnitsBulkAddressInit: (state, action: PayloadAction<{ files: File[] }>) => {
      state.loading.import = true;
      state.status = null;
    },
    importUnitsBulkAddressSuccess: state => {
      state.loading.import = false;
      state.status = 'address-imported';
    },
    importUnitsBulkAddressFailed: state => {
      state.loading.import = false;
    },

    // system: create Database Dump
    createDatabaseDumpInit: () => {},
    createDatabaseDumpFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.error.tabs.system = payload;
    },

    // system: fetch Database Dumps
    fetchDatabaseDumpsInit: state => {
      state.loading.system['database-dump'] = true;
    },
    fetchDatabaseDumpsSuccess: (state, action: PayloadAction<DatabaseDump[]>) => {
      state.loading.system['database-dump'] = false;
      state.data.system['database-dump'].list = action.payload;
    },
    fetchDatabaseDumpsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system['database-dump'] = false;
      state.error.tabs.system = payload;
    },

    fetchDatabaseDumpReplicationSettingsInit: state => {
      state.loading.system['database-dump'] = true;
    },
    fetchDatabaseDumpReplicationSettingsSuccess: (state, action: PayloadAction<DatabaseDumpReplicationSettings>) => {
      state.loading.system['database-dump'] = false;
      state.data.system['database-dump'].sync = action.payload;
    },

    updateDatabaseDumpReplicationSettingsInit:
      (state, { payload }: PayloadAction<{ values: DatabaseDumpReplicationSettings & { password: string }}>) => {
        state.loading.system['database-dump'] = true;
        state.status = null;
        state.error.tabs.system = null;
      },

    updateDatabaseDumpReplicationSettinsSuccess: state => {
      state.loading.system['database-dump'] = false;
      state.status = 'updated';
    },

    updateDatabaseDumpReplicationSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system['database-dump'] = false;
      state.error.tabs.system = payload;
    },

    databaseDumpReplicationStartNowInit: state => {
      state.loading.system['database-dump'] = true;
    },

    databaseDumpReplicationStartNowSuccess: state => {
      state.loading.system['database-dump'] = false;
    },

    databaseDumpReplicationStarNowFailed: state => {
      state.loading.system['database-dump'] = false;
    },

    databaseDumpReplicationReverseStartNowInit: (state, { payload }: PayloadAction<{ password: string }>) => {
      state.loading.system['database-dump'] = true;
      state.status = null;
      state.error.tabs.system = null;
    },

    databaseDumpReplicationReverseStartNowSuccess: state => {
      state.loading.system['database-dump'] = false;
      state.status = 'started';
    },

    databaseDumpReplicationReverseStarNowFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system['database-dump'] = false;
      state.error.tabs.system = payload;
    },

    fetchDatabaseDumpHistoryInit: state => {
      state.loading.system['database-dump'] = true;
    },

    fetchDatabaseDumpHistorySuccess: (state, action: PayloadAction<DatabaseDumpHistory[]>) => {
      state.loading.system['database-dump'] = false;
      state.data.system['database-dump'].history = action.payload;
    },

    fetchDatabaseDumpHistoryFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system['database-dump'] = false;
      state.error.tabs.system = payload;
    },

    // system: fetch Database Dump by id
    fetchDatabaseDumpByIdInit: (state, action: PayloadAction<string>) => {},
    fetchDatabaseDumpByIdSuccess: state => {
      state.data.system['database-dump'].progress.completed = true;
    },
    fetchDatabaseDumpByIdFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.error.tabs.system = payload;
      state.data.system['database-dump'].progress.completed = true;
    },

    // system: set dB dump progress
    setDatabaseDumpProgress: (state, { payload: { loaded, total } }) => {
      state.data.system['database-dump'].progress.loaded = loaded;
      state.data.system['database-dump'].progress.total = total;
      state.data.system['database-dump'].progress.completed = false;
    },

    // system: fetch scheduled DB Dump status
    fetchScheduledDBDumpStatusInit: state => {
      state.loading.system['database-dump'] = true;
    },
    fetchScheduledDBDumpStatusSuccess: (state, action: PayloadAction<ScheduledDBDumpStatus>) => {
      state.loading.system['database-dump'] = false;
      state.data.system['database-dump'].status = action.payload;
    },
    fetchScheduledDBDumpStatusFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system['database-dump'] = false;
      state.error.tabs.system = payload;
    },

    // system: schedule DB Dump
    scheduleDBDumpInit: (state, action: PayloadAction<ScheduledDBDumpStatus>) => {
      state.loading.system['database-dump'] = true;
    },

    // network: update tcp
    updateTcpSettingsInit: (state, { payload }: PayloadAction<{ action: string, values: TcpSettings }>) => {
      state.loading.network.tcp = true;
    },

    // network: update protocol
    updateProtocolSettingsInit: (state, { payload }: PayloadAction<{ values: PortConfiguration }>) => {
      state.loading.network.protocol = true;
    },

    // Execute sub-tools
    executeSubtoolsJobInit(
      state,
      {
        payload: { section },
      }: PayloadAction<{
        section: SubToolsExecuteSection;
        values: Partial<SubToolsSettings['firstData'] | SubToolsSettings['refreshData']>;
      }>
    ) {
      state.loading['sub-tools'][section] = true;
    },
    executeSubtoolsJobSuccess(state, { payload: { section } }: PayloadAction<{ section: SubToolsExecuteSection }>) {
      state.loading['sub-tools'][section] = false;
    },
    executeSubtoolsJobFailed(
      state,
      { payload: { section } }: PayloadAction<{ section: SubToolsExecuteSection; error: ResponseError }>
    ) {
      state.loading['sub-tools'][section] = false;
    },

    // update check-in grace period
    updateCheckInGracePeriodInit<T>(state, { payload }: PayloadAction<{ values: T }>) {
      state.loading.tabs['check-in'] = true;
      state.status = null;
      state.error.tabs = initialState.error.tabs;
    },
    updateCheckInGracePeriodSuccess(state) {
      state.status = 'updated';
    },
    updateCheckInGracePeriodFailed(
      state,
      { payload }: PayloadAction<ResponseError>
    ) {
      state.loading.tabs['check-in'] = false;
      state.status = null;
      state.error.tabs['check-in'] = payload;
    },

    // fetch antennas
    fetchAntennasInit(state) {
      state.data.antennas.status = null;
      state.error.tabs = initialState.error.tabs;
      state.data.antennas.list = initialState.data.antennas.list;
      state.loading.antennas.list = true;
    },
    fetchAntennasSuccess<T>(state, { payload: { values } }: PayloadAction<{ values: T }>) {
      state.loading.antennas.list = false;
      state.data.antennas.list = values;
    },
    fetchAntennasFail(state, { payload: { error } }: PayloadAction<{ error: ResponseError }>) {
      state.loading.antennas.list = false;
      state.error.tabs.antennas = error;
    },

    // fetch antennas names
    fetchAntennasNamesInit(state) {
      state.data.antennas.status = null;
      state.error.tabs = initialState.error.tabs;
      state.data.antennas.names = initialState.data.antennas.names;
      state.loading.antennas.list = true;
    },
    fetchAntennasNamesSuccess(state, { payload }: PayloadAction<string[]>) {
      state.loading.antennas.list = false;
      state.data.antennas.names = payload;
    },

    // delete antenna
    deleteAntennaInit(state, { payload }: PayloadAction<{ id: Antenna['id']; name: Antenna['name'] }>) {
      state.loading.antennas.delete = true;
    },
    deleteAntennaSuccess(state) {
      state.loading.antennas.delete = false;
    },
    deleteAntennaFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.antennas.delete = false;
      state.error.tabs.antennas = payload;
    },

    // add antenna
    addAntennaInit(state, { payload }: PayloadAction<{ antennaName: Antenna['name'] }>) {
      state.loading.antennas.add = true;
    },
    addAntennaSuccess(state) {
      state.loading.antennas.add = false;
      state.data.antennas.status = 'created';
    },
    addAntennaFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.antennas.add = false;
      state.error.tabs.antennas = payload;
    },

    // maintenance: SSL certificate request
    createSSLRequestInit(state, { payload }: PayloadAction<SSLRequest>) {
      state.loading.maintenance['ssl-created'] = true;
    },
    createSSLRequestSuccess(state) {
      state.loading.maintenance['ssl-created'] = false;
      state.status = 'ssl-created';
    },
    createSSLRequestFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.maintenance['ssl-created'] = false;
      state.error.tabs.maintenance = payload;
    },

    // maintenance: upload SSL files
    upLoadSSLFilesInit(state, { payload: { files } }: PayloadAction<{ files: File[] }>) {
      state.loading.maintenance['ssl-upload'] = true;
      state.status = null;
    },
    upLoadSSLFilesSuccess(state) {
      state.loading.maintenance['ssl-upload'] = false;
      state.status = 'updated';
    },
    upLoadSSLFilesFailed(state) {
      state.loading.maintenance['ssl-upload'] = false;
    },

    // maintenance: fetch Weekly Report Timestamp
    fetchWeeklyReportSettingsInit: state => {
      state.loading.maintenance.weeklyReport = true;
    },
    fetchWeeklyReportSettingsSuccess: (state, { payload }: PayloadAction<{ dateTimeForNextWeeklyReport: number }>) => {
      state.loading.maintenance.weeklyReport = false;
      state.data.maintenance.weeklyReportTimestamp = payload.dateTimeForNextWeeklyReport;
      state.status = 'updated';
    },
    fetchWeeklyReportSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.maintenance.weeklyReport = false;
      state.error.tabs.maintenance = payload;
    },

    // maintenance: update Weekly Report
    updateWeeklyReportSettingsInit: (state, { payload }: PayloadAction<{ dateTimeForNextWeeklyReport: number }>) => {
      state.loading.maintenance.weeklyReport = true;
    },

    // selfMonitoring: Fetch Web Relay Settings
    fetchWebRelaySettingsInit: state => {
      state.loading.selfMonitoring.webRelay = true;
    },
    fetchWebRelaySettingsSuccess(state, { payload: { values } }: PayloadAction<{ values: WebRelay }>) {
      state.loading.selfMonitoring.webRelay = false;
      state.data.selfMonitoring.webRelay = values;
    },
    fetchWebRelaySettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.webRelay = false;
      state.error.selfMonitoring.webRelay = payload;
    },

    updateWebRelaySettingsInit(state, { payload }: PayloadAction<{ values: WebRelay }>) {
      state.loading.selfMonitoring.webRelay = true;
    },

    // selfMonitoring: fetch Web Relay Monitor Settings
    fetchWebRelayMonitorSettingsInit: state => {
      state.loading.selfMonitoring.webRelay = true;
    },
    fetchWebRelayMonitorSettingsSuccess: (state, { payload }: PayloadAction<{ isMonitored: boolean }>) => {
      state.loading.selfMonitoring.webRelay = false;
      state.data.selfMonitoring.isMonitored = payload.isMonitored;
    },
    fetchWebRelayMonitorSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.webRelay = false;
      state.error.selfMonitoring.webRelay = payload;
    },

    // selfMonitoring: update Web Relay Monitor Settings
    updateWebRelayMonitorSettingsInit: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.selfMonitoring.webRelay = true;
    },

    // selfMonitoring: fetch High Contrast Settings
    fetchHighContrastSettingsInit: state => {
      state.loading.selfMonitoring.highContrast = true;
    },
    fetchHighContrastSettingsSuccess: (state, { payload }: PayloadAction<{ highContrast: boolean }>) => {
      state.loading.selfMonitoring.highContrast = false;
      state.data.selfMonitoring.highContrast = payload.highContrast;
    },
    fetchHighContrastSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.highContrast = false;
      state.error.selfMonitoring.highContrast = payload;
    },

    // selfMonitoring: update High Contrast Settings
    updateHighContrastSettingsInit: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.selfMonitoring.highContrast = true;
    },

    fetchStormModeSettingsInit: state => {
      state.loading.selfMonitoring.stormMode = true;
    },
    fetchStormModeSettingsSuccess: (state, { payload }: PayloadAction<{ isEnabled: boolean }>) => {
      state.loading.selfMonitoring.stormMode = false;
      state.data.selfMonitoring.stormMode = payload.isEnabled;
    },
    fetchStormModeSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.stormMode = false;
      state.error.selfMonitoring.stormMode = payload;
    },

    updateStormModeSettingsInit: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.selfMonitoring.stormMode = true;
    },

    fetchAlarmHistoryLifeTimeSettingsInit: state => {
      state.loading.selfMonitoring.alarmHistoryLifeTime = true;
    },

    fetchAlarmHistoryLifeTimeSettingsSuccess: (state, { payload }: PayloadAction<{ days: number }>) => {
      state.loading.selfMonitoring.alarmHistoryLifeTime = false;
      state.data.selfMonitoring.alarmHistoryLifeTime = payload.days;
    },

    fetchAlarmHistoryLifeTimeSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.alarmHistoryLifeTime = false;
      state.error.selfMonitoring.alarmHistoryLifeTime = payload;
    },

    updateAlarmHistoryLifeTimeSettingsInit: (state, { payload }: PayloadAction<{ days: number }>) => {
      state.loading.selfMonitoring.alarmHistoryLifeTime = true;
    },

    // system: update auto-supervision
    updateAutoSupervisionSettingsInit: (state, { payload }: PayloadAction<{ supervisionEnable: boolean }>) => {
      state.loading.system['auto-supervision'] = true;
      state.status = null;
    },

    updateAutoSupervisionSettingsSuccess(state) {
      state.loading.system['auto-supervision'] = false;
      state.status = 'auto-supervision';
    },

    // selfMonitoring: fetch Hydrant Radius Settings
    fetchHydrantRadiusSettingsInit: state => {
      state.loading.selfMonitoring.hydrantRadius = true;
    },
    fetchHydrantRadiusSettingsSuccess: (state, {
      payload: { values }
    }: PayloadAction<{ values: HydrantRadiusSettings }>) => {
      state.loading.selfMonitoring.hydrantRadius = false;
      state.data.selfMonitoring.hydrantRadius = values;
    },
    fetchHydrantRadiusSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.hydrantRadius = false;
      state.error.selfMonitoring.hydrantRadius = payload;
    },

    updateHydrantRadiusSettingsInit: (state, { payload }: PayloadAction<{ values: HydrantRadiusSettings }>) => {
      state.loading.selfMonitoring.hydrantRadius = true;
    },

    fetchNotificationsStatusInit: state => {
      state.loading.system.notifications = true;
      state.data.system.notificationsStatus = null;
    },

    fetchNotificationsStatusSuccess: (state, { payload }: PayloadAction<{ sendNotification: boolean }>) => {
      state.loading.system.notifications = false;
      state.data.system.notificationsStatus = payload.sendNotification;
    },

    fetchNotificationsStatusFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.system.notifications = false;
      state.error.tabs.system = payload;
    },

    fetchAutoAckSettingsInit: state => {
      state.loading.selfMonitoring.autoAck = true;
    },
    fetchAutoAckSettingsSuccess(state, { payload: { values } }: PayloadAction<{ values: AutoAckSettings[] }>) {
      state.loading.selfMonitoring.autoAck = false;
      state.data.selfMonitoring.autoAckSettings = values;
    },
    fetchAutoAckSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.autoAck = false;
      state.error.selfMonitoring.autoAck = payload;
    },

    updateAutoAckSettingsInit(state, { payload: { values } }: PayloadAction<{ values: Partial<AutoAckSettings> }>) {
      state.loading.selfMonitoring.autoAck = true;
    },
    updateAutoAckSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.autoAck = false;
      state.error.selfMonitoring.autoAck = payload;
    },

    deleteAutoAckSettingsInit(state, { payload: { id } }: PayloadAction<{ id: AutoAckSettings['id'] }>) {
      state.loading.selfMonitoring.autoAck = true;
    },
    deleteAutoAckSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.autoAck = false;
      state.error.selfMonitoring.autoAck = payload;
    },

    updateNetworkInstancesInit<T>(state, { payload }: PayloadAction<{ values: T}>) {
      state.loading.network.instances = true;
    },

    updateNetworkInstancesSuccess: state => {
      state.loading.network.instances = false;
      state.status = 'instances-updated';
    },

    updateNetworkInstancesFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.network.instances = false;
      state.error.instances = payload;
    },

    fetchAppLogsInit: state => {
      state.loading.system['incc-logs'] = true;
    },

    fetchAppLogsSuccess(state, { payload: { values } }: PayloadAction<{ values: INCCLogFiles[] }>) {
      state.loading.system['incc-logs'] = false;
      state.data.system.appLogs = values;
    },

    inccLogsFailed: state => {
      state.loading.system['incc-logs'] = false;
    },

    fetchDbLogsInit: state => {
      state.loading.system['incc-logs'] = true;
    },

    fetchDbLogsSuccess(state, { payload: { values } }: PayloadAction<{ values: INCCLogFiles[] }>) {
      state.loading.system['incc-logs'] = false;
      state.data.system.dbLogs = values;
    },

    downloadINCCLogFileInit:
      (state, { payload: { fileName, isAppLog } }: PayloadAction<{ fileName: string, isAppLog: boolean }>) => {
        state.loading.system['incc-logs'] = true;
      },

    downloadINCCLogSuccess: state => {
      state.loading.system['incc-logs'] = false;
    },

    fetchAutoAckEventSettingsInit: state => {
      state.loading.selfMonitoring.autoAck = true;
    },
    fetchAutoAckEventSettingsSuccess(state, { payload: { values } }: PayloadAction<{ values: AutoAckEventTypes }>) {
      state.loading.selfMonitoring.autoAck = false;
      state.data.selfMonitoring.autoAckEventTypes = values;
    },
    fetchAutoAckEventSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.autoAck = false;
      state.error.selfMonitoring.autoAck = payload;
    },

    updateAutoAckEventSettingsInit(state, { payload: { values } }: PayloadAction<{ values: AutoAckEventTypes }>) {
      state.loading.selfMonitoring.autoAck = true;
    },
    updateAutoAckEventSettingsFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.selfMonitoring.autoAck = false;
      state.error.selfMonitoring.autoAck = payload;
    },

    sendOutboundEmailInit(state, { payload: { values } }: PayloadAction<{ values: SMTPOutboundEmailTest }>) {
      state.loading.sendOutbound = true;
    },
    testEmailConnectionInit(state) {
      state.loading.sendOutbound = true;
    },
    testEmailConnectionFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.sendOutbound = false;
    },

    // Reset settings
    reset(state) {
      Object.assign(state, initialState);
    },

    // Reset status
    resetStatus(state) {
      state.status = null;
    },
  },
  extraReducers: {
    [sharedActions.reset.toString()]: state => Object.assign(state, initialState),
  },
});

const getSettingsState = (state: AES.RootState) => state.settings;

export const selectors = {
  getSettingsValues: <T>(tab: SettingsTab) =>
    createDraftSafeSelector(getSettingsState, state => (state.data[tab] as unknown) as T),

  getSettingsError: (tab: SettingsTab) => createDraftSafeSelector(getSettingsState, state => state.error.tabs[tab]),
  getSettingsLoaders: createDraftSafeSelector(getSettingsState, state => state.loading),
  getSettingsStatus: createDraftSafeSelector(getSettingsState, state => state.status),
  getSystemSettingsValues: <T>(section: SystemSettingsSection) =>
    createDraftSafeSelector(getSettingsState, state => (state.data.system[section] as unknown) as T),
  getServerSettingsValues: <T>(section: ServerSettingsSection) =>
    createDraftSafeSelector(getSettingsState, state => (state.data.server[section] as unknown) as T),
  getNetworkSettingsValues: <T>(section: NetworkSettingsSection) =>
    createDraftSafeSelector(getSettingsState, state => (state.data.network[section] as unknown) as T),
  getAntennaValues: createDraftSafeSelector(getSettingsState, state => state.data.antennas),
  getDatabaseDumps: createDraftSafeSelector(getSettingsState, state => state.data.system?.['database-dump']),
  getDatabaseDumpProgress: createDraftSafeSelector(getSettingsState, state => state.data.system?.['database-dump'].progress),
  getDatabaseDumpReplicationSettings: createDraftSafeSelector(getSettingsState, state => state.data.system?.['database-dump'].sync),
  getDatabaseDumpHistory: createDraftSafeSelector(getSettingsState, state => state.data.system?.['database-dump'].history),
  areAddressImported: createDraftSafeSelector(getSettingsState, state => state.status === 'address-imported'),
  getWeeklyReportSettings: createDraftSafeSelector(
    getSettingsState, state => state.data.maintenance.weeklyReportTimestamp
  ),
  getSelfMonitoringSettings: createDraftSafeSelector(getSettingsState, state => state.data.selfMonitoring),
  getSelfMonitoringError: createDraftSafeSelector(getSettingsState, state => state.error.selfMonitoring),
  isUpdated: createDraftSafeSelector(getSettingsState, state => state.status === 'updated'),
  isDatabaseDumpStarted: createDraftSafeSelector(getSettingsState, state => state.status === 'started'),
  getNotificationsStatus: createDraftSafeSelector(getSettingsState, state => state.data.system.notificationsStatus),
  getNetworkInstancesError: createDraftSafeSelector(getSettingsState, state => state.error.instances),
  isInstancesUpdated: createDraftSafeSelector(getSettingsState, state => state.status === 'instances-updated'),
  getAppLogs: createDraftSafeSelector(getSettingsState, state => state.data.system.appLogs),
  getDbLogs: createDraftSafeSelector(getSettingsState, state => state.data.system.dbLogs),
};

const fetchSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchSettingsInit.match),
    mergeMap(({ payload: { tab } }) => {
      const { url } = getSettingsRequestConfig(tab);

      return http.getJSON(url).pipe(
        mergeMap(values => of(actions.fetchSettingsSuccess({ tab, values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchSettingsFailed({ tab, error }));
        })
      );
    })
  );

const updateSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateSettingsInit.match),
    mergeMap(({ payload: { tab, values } }) => {
      const { url, title, handler } = getSettingsRequestConfig(tab);

      return http[handler](url, values).pipe(
        mergeMap(() => {
          const epicActions: AnyAction[] = [
            actions.updateSettingsSuccess(),
            actions.fetchSettingsInit({ tab }),
            notificationsActions.enqueue({
              message: `${title} settings updated`,
              options: { variant: 'success' },
            }),
          ];
          if (tab === 'integrations') {
            epicActions.push(
              appActions.fetchSystemInfoInit()
            );
          }
          return of(...epicActions);
        }),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateSettingsFailed({ tab, error }));
        })
      );
    })
  );

const fetchSystemSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchSystemSettingsInit.match),
    mergeMap(({ payload: { section } }) => {
      const { url } = getSystemSettingsRequestConfig(section);

      return http.getJSON(url).pipe(
        mergeMap(values => of(actions.fetchSystemSettingsSuccess({ section, values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchSystemSettingsFailed({ section, error }));
        })
      );
    })
  );

const updateSystemSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateSystemSettingsInit.match),
    mergeMap(({ payload: { section, values } }) => {
      const { url, title, handler } = getSystemSettingsRequestConfig(section);

      return http[handler](url, values).pipe(
        mergeMap(() =>
          of(
            appActions.fetchSystemInfoInit(),
            actions.updateSystemSettingsSuccess(),
            notificationsActions.enqueue({
              message: `${title} settings updated`,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateSystemSettingsFailed({ error }));
        })
      );
    })
  );

const fetchServerSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchServerSettingsInit.match),
    mergeMap(({ payload: { section } }) => {
      const { url } = getServerSettingsRequestConfig(section);

      return http.getJSON(url).pipe(
        mergeMap(values => of(actions.fetchServerSettingsSuccess({ section, values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchServerSettingsFailed({ section, error }));
        })
      );
    })
  );

const updateServerSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateServerSettingsInit.match),
    mergeMap(({ payload: { section, values } }) => {
      const { url, title, handler } = getServerSettingsRequestConfig(section);

      return http[handler](url, values).pipe(
        mergeMap(() =>
          of(
            appActions.fetchSystemInfoInit(),
            actions.updateServerSettingsSuccess({ section }),
            notificationsActions.enqueue({
              message: `${title} settings updated`,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateServerSettingsFailed({ error, section }));
        })
      );
    })
  );

const fetchNetworkSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchNetworkSettingsInit.match),
    mergeMap(({ payload: { section } }) => {
      const { url } = getNetworkSettingsRequestConfig(section);

      return http.getJSON(url).pipe(
        mergeMap(values => of(actions.fetchNetworkSettingsSuccess({ section, values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchNetworkSettingsFailed({ section, error }));
        })
      );
    })
  );

const updateNetworkSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateNetworkSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.put(api.settings.network, values).pipe(
        mergeMap(({ response }) => {
          const epicActions: AnyAction[] = [
            actions.updateNetworkSettingsSuccess({ ipChanged: response?.ipChanged, values }),
          ];

          if (response?.message) {
            epicActions.push(
              notificationsActions.enqueue({
                message: response?.message,
                options: { variant: 'success' },
              })
            );
          }
          return of(...epicActions);
        }),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateNetworkSettingsFailed({ error }));
        })
      )
    )
  );

const updateIntegrationSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateIntegrationSettingsInit.match),
    mergeMap(({ payload: { tab, values } }) => {
      const { url, title, handler } = getSettingsRequestConfig(tab);

      return http[handler](url, values).pipe(
        mergeMap(() =>
          of(
            appActions.fetchSystemInfoInit(),
            actions.updateIntegrationSettingsSuccess(),
            actions.fetchSettingsInit({ tab }),
            notificationsActions.enqueue({
              message: `${title} settings updated`,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateIntegrationSettingsFailed({ tab, error }));
        })
      );
    })
  );

const importUnitsBulkAddressEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AES.RootState>) =>
  action$.pipe(
    filter(actions.importUnitsBulkAddressInit.match),
    mergeMap(({ payload: { files } }) => {
      const formData = new FormData();

      files.forEach(file => formData.append('file', file, file.name));

      return ajax({
        method: 'POST',
        url: api.settings.import,
        body: formData,
        headers: {
          Authorization: `Bearer ${state$.value.auth.accessToken}`,
        },
        responseType: 'text/plain',
      }).pipe(
        delay(300),
        mergeMap(res =>
          of(
            actions.importUnitsBulkAddressSuccess(),
            notificationsActions.enqueue({
              message: res.response,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err =>
          of(
            actions.importUnitsBulkAddressFailed(),
            notificationsActions.enqueue({ message: err.response, options: { variant: 'error' } })
          )
        )
      );
    })
  );

export const executeSubtoolsJobEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.executeSubtoolsJobInit.match),
    mergeMap(({ payload: { section, values } }) =>
      http.post(api.settings.executeSubTools(section), values).pipe(
        mergeMap(() =>
          of(
            actions.executeSubtoolsJobSuccess({ section }),
            notificationsActions.enqueue({
              message: `Subtools ${section} job execution started`,
              options: {
                variant: 'success',
              },
            })
          )
        ),
        catchError(err => of(actions.executeSubtoolsJobFailed({ section, error: getResponseError(err) })))
      )
    )
  );

const updateCheckInGracePeriodEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateCheckInGracePeriodInit.match),
    mergeMap(({ payload: { values } }) => {
      const { url, title, handler } = getSettingsRequestConfig('check-in');

      return http[handler](url, values).pipe(
        mergeMap(() =>
          of(
            actions.updateCheckInGracePeriodSuccess(),
            actions.fetchSettingsInit({ tab: 'check-in' }),
            notificationsActions.enqueue({
              message: `${title} period updated`,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateCheckInGracePeriodFailed(error));
        })
      );
    })
  );

const fetchAntennaSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAntennasInit.match),
    mergeMap(() =>
      http.getJSON(api.antenna.all).pipe(
        mergeMap(values => of(actions.fetchAntennasSuccess({ values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAntennasFail({ error }));
        })
      )
    )
  );

const fetchAntennaNamesEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAntennasNamesInit.match),
    mergeMap(() =>
      http.getJSON<string[]>(api.antenna.list).pipe(
        mergeMap(values => of(actions.fetchAntennasNamesSuccess(values))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAntennasFail({ error }));
        })
      )
    )
  );

const deleteAntennaEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteAntennaInit.match),
    mergeMap(({ payload }) =>
      http.delete(api.antenna.byId(payload.id)).pipe(
        mergeMap(() =>
          of(
            actions.deleteAntennaSuccess(),
            notificationsActions.enqueue({
              message: `Antenna ${payload.name} was deleted`,
              options: { variant: 'success' },
            }),
            actions.fetchAntennasInit()
          )
        ),
        catchError(err => of(actions.deleteAntennaFailed(getResponseError(err))))
      )
    )
  );

const addAntennaEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.addAntennaInit.match),
    mergeMap(({ payload: { antennaName } }) =>
      http.post(api.antenna.all, { antennaName }).pipe(
        mergeMap(res =>
          of(
            actions.addAntennaSuccess(),
            notificationsActions.enqueue({
              message: `Antenna ${antennaName} added to the list`,
              options: { variant: 'success' },
            }),
            actions.fetchAntennasInit()
          )
        ),
        catchError(err => of(actions.addAntennaFailed(getResponseError(err))))
      )
    )
  );

const createSSLRequestEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.createSSLRequestInit.match),
    mergeMap(({ payload }) =>
      http
        .call({
          method: 'POST',
          url: api.settings.maintenance,
          body: payload,
          responseType: 'blob' as 'json',
        })
        .pipe(
          mergeMap(res => {
            const fileName = 'incc.csr';
            saveFile(res.response, fileName);

            return of(
              actions.createSSLRequestSuccess(),
              notificationsActions.enqueue({
                message: `${fileName} has been downloaded`,
                options: { variant: 'success' },
              })
            );
          }),
          catchError(err => of(actions.createSSLRequestFailed(getResponseError(err))))
        )
    )
  );

const upLoadSSLFilesEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AES.RootState>) =>
  action$.pipe(
    filter(actions.upLoadSSLFilesInit.match),
    mergeMap(({ payload: { files } }) => {
      const formData = new FormData();

      files.forEach(file => {
        if (file.name.endsWith('.crt')) {
          formData.append('crtFile', file, file.name);
        }

        if (file.name.endsWith('.key')) {
          formData.append('sslFile', file, file.name);
        }
      });

      return ajax({
        method: 'POST',
        url: api.settings.sslFilesUpload,
        body: formData,
        headers: {
          Authorization: `Bearer ${state$.value.auth.accessToken}`,
        },
      }).pipe(
        mergeMap(() =>
          of(
            actions.upLoadSSLFilesSuccess(),
            notificationsActions.enqueue({
              message: 'SSL file(s) uploaded',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err =>
          of(
            actions.upLoadSSLFilesFailed(),
            notificationsActions.enqueue({
              message: getResponseError(err).message,
              options: { variant: 'error' },
            })
          )
        )
      );
    })
  );

const createDatabaseDumpEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.createDatabaseDumpInit.match),
    mergeMap(() =>
      http.post(api.system.databaseDumps.all).pipe(
        mergeMap(() => of(
          notificationsActions.enqueue({
            message: 'Database dump creating started',
            options: { variant: 'success' },
          })
        )),
        catchError(err => of(
          actions.createDatabaseDumpFailed(getResponseError(err))
        ))
      )
    )
  );

const fetchDatabaseDumpsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchDatabaseDumpsInit.match),
    mergeMap(() =>
      http.getJSON<DatabaseDump[]>(api.system.databaseDumps.all).pipe(
        mergeMap(res => of(actions.fetchDatabaseDumpsSuccess(res))),
        catchError(err => of(
          actions.fetchDatabaseDumpsFailed(getResponseError(err))
        ))
      )
    )
  );

const fetchDatabaseDumpByIdEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchDatabaseDumpByIdInit.match),
    mergeMap(({ payload }) =>
      http
        .call({
          method: 'GET',
          url: api.system.databaseDumps.byId(payload),
          responseType: 'blob' as 'json',
          createXHR: () => {
            const xhr = new XMLHttpRequest();
            xhr.onprogress = ({ loaded, total }) => {
              boundActions.settings.setDatabaseDumpProgress({
                total,
                loaded,
              });
            };
            return xhr;
          },
        })
        .pipe(
          mergeMap(res => {
            saveFile(res.response, payload);

            return of(
              actions.fetchDatabaseDumpByIdSuccess(),
              notificationsActions.close('db-dump-requested'),
            );
          }),
          catchError(err => {
            const error = getResponseError(err);

            return of(actions.fetchDatabaseDumpByIdFailed(error));
          })
        )
    )
  );

const fetchScheduledDBDumpStatusEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchScheduledDBDumpStatusInit.match),
    mergeMap(() =>
      http.getJSON<ScheduledDBDumpStatus>(api.system.databaseDumps.scheduledStatus).pipe(
        mergeMap(res => of(actions.fetchScheduledDBDumpStatusSuccess(res))),
        catchError(err => of(
          actions.fetchScheduledDBDumpStatusFailed(getResponseError(err))
        ))
      )
    )
  );

const scheduleDBDumpEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.scheduleDBDumpInit.match),
    mergeMap(({ payload }) =>
      http.patch(api.system.databaseDumps.schedule(payload)).pipe(
        mergeMap(() => of(actions.fetchScheduledDBDumpStatusSuccess(payload))),
        catchError(err => of(
          actions.fetchScheduledDBDumpStatusFailed(getResponseError(err))
        ))
      )
    )
  );

const fetchDatabaseDumpReplicationSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchDatabaseDumpReplicationSettingsInit.match),
    mergeMap(() =>
      http.getJSON<DatabaseDumpReplicationSettings>(api.system.databaseDumps.sync).pipe(
        mergeMap(res => of(actions.fetchDatabaseDumpReplicationSettingsSuccess(res))),
        catchError(err => of(
          actions.fetchDatabaseDumpsFailed(getResponseError(err))
        ))
      )
    )
  );

const updateDatabaseDumpReplicationSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateDatabaseDumpReplicationSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.post(api.system.databaseDumps.sync, values).pipe(
        mergeMap(res =>
          of(
            actions.updateDatabaseDumpReplicationSettinsSuccess(),
            actions.fetchDatabaseDumpReplicationSettingsInit(),
            notificationsActions.enqueue({
              message: 'Replication Settings have been updated',
              options: {
                variant: 'success',
              },
            })
          )
        ),
        catchError(err => of(actions.updateDatabaseDumpReplicationSettingsFailed(getResponseError(err))))
      )
    )
  );

const databaseDumpReplicationStartNowEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.databaseDumpReplicationStartNowInit.match),
    mergeMap(() =>
      http.post(api.system.databaseDumps.startNow).pipe(
        mergeMap(res =>
          of(
            actions.databaseDumpReplicationStartNowSuccess(),
            notificationsActions.enqueue({
              message: 'Database dump creating started“',
              options: {
                variant: 'success',
              },
            })
          )
        ),
        catchError(() => of(actions.databaseDumpReplicationStarNowFailed()))
      )
    )
  );

const databaseDumpReplicationReverseStartNowEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.databaseDumpReplicationReverseStartNowInit.match),
    mergeMap(({ payload: { password } }) =>
      http.post(api.system.databaseDumps.reverseStartNow, { password }).pipe(
        mergeMap(res =>
          of(
            actions.databaseDumpReplicationReverseStartNowSuccess(),
            notificationsActions.enqueue({
              message: 'Reverse Database dump started“',
              options: {
                variant: 'success',
              },
            })
          )
        ),
        catchError(err => of(actions.databaseDumpReplicationReverseStarNowFailed(getResponseError(err))))
      )
    )
  );

const fetchDatabaseDumpHistoryEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchDatabaseDumpHistoryInit.match),
    mergeMap(() =>
      http.getJSON<DatabaseDumpHistory[]>(api.system.databaseDumps.history).pipe(
        mergeMap(res => of(actions.fetchDatabaseDumpHistorySuccess(res))),
        catchError(err => of(
          actions.fetchDatabaseDumpHistoryFailed(getResponseError(err))
        ))
      )
    )
  );
const fetchWeeklyReportSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchWeeklyReportSettingsInit.match),
    mergeMap(() =>
      http.getJSON<{ dateTimeForNextWeeklyReport: number }>(api.settings.weeklyReport).pipe(
        mergeMap(values => of(
          actions.fetchWeeklyReportSettingsSuccess({ dateTimeForNextWeeklyReport: values.dateTimeForNextWeeklyReport })
        )),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWeeklyReportSettingsFailed(error));
        })
      )
    )
  );

const updateWeeklyReportSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateWeeklyReportSettingsInit.match),
    mergeMap(({ payload: { dateTimeForNextWeeklyReport } }) =>
      http.patch(api.settings.weeklyReport, { dateTimeForNextWeeklyReport }).pipe(
        mergeMap(() =>
          of(
            actions.fetchWeeklyReportSettingsSuccess({ dateTimeForNextWeeklyReport }),
            actions.fetchWeeklyReportSettingsInit(),
            profileActions.updateWeeklyReportSettings({ dateTimeForNextWeeklyReport }),
            notificationsActions.enqueue({
              message: 'Weekly Report Timing Updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWeeklyReportSettingsFailed(error));
        })
      )
    )
  );

const fetchWebRelaySettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchWebRelaySettingsInit.match),
    mergeMap(() =>
      http.getJSON<WebRelay>(api.settings.selfMonitoring.webRelay).pipe(
        mergeMap(values => of(actions.fetchWebRelaySettingsSuccess({ values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWebRelaySettingsFailed(error));
        })
      )
    )
  );

const updateWebRelaySettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateWebRelaySettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.patch(api.settings.selfMonitoring.webRelay, values).pipe(
        mergeMap(() =>
          of(
            actions.fetchWebRelaySettingsSuccess({ values }),
            notificationsActions.enqueue({
              message: 'Web Relay Settings updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWebRelaySettingsFailed(error));
        })
      )
    )
  );

const fetchWebRelayMonitorSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchWebRelayMonitorSettingsInit.match),
    mergeMap(() =>
      http.getJSON<{isMonitored: boolean}>(api.settings.selfMonitoring.monitor).pipe(
        mergeMap(values => of(actions.fetchWebRelayMonitorSettingsSuccess(values))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWebRelayMonitorSettingsFailed(error));
        })
      )
    )
  );

const updateWebRelayMonitorSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateWebRelayMonitorSettingsInit.match),
    mergeMap(({ payload }) =>
      http.patch(api.settings.selfMonitoring.monitor, { isMonitored: payload }).pipe(
        mergeMap(() =>
          of(
            actions.fetchWebRelayMonitorSettingsSuccess({ isMonitored: payload }),
            notificationsActions.enqueue({
              message: 'Pulse Monitor Settings updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchWebRelayMonitorSettingsFailed(error));
        })
      )
    )
  );

const fetchHighContrastSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchHighContrastSettingsInit.match),
    mergeMap(() =>
      http.getJSON<{ highContrast: boolean }>(api.settings.selfMonitoring.highContrast).pipe(
        mergeMap(values => of(actions.fetchHighContrastSettingsSuccess(values))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchHighContrastSettingsFailed(error));
        })
      )
    )
  );

const updateHighContrastSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateHighContrastSettingsInit.match),
    mergeMap(({ payload }) =>
      http.patch(api.settings.selfMonitoring.highContrast, { highContrast: payload }).pipe(
        mergeMap(() =>
          of(
            actions.fetchHighContrastSettingsSuccess({ highContrast: payload }),
            notificationsActions.enqueue({
              message: 'Interface Settings updated',
              options: { variant: 'success' },
            }),
            appActions.fetchSystemInfoInit()
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchHighContrastSettingsFailed(error));
        })
      )
    )
  );

const fetchStormModeSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchStormModeSettingsInit.match),
    mergeMap(() =>
      http.getJSON<{ isEnabled: boolean }>(api.settings.selfMonitoring.stormMode).pipe(
        mergeMap(values => of(actions.fetchStormModeSettingsSuccess(values))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchStormModeSettingsFailed(error));
        })
      )
    )
  );

const updateStormModeSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateStormModeSettingsInit.match),
    mergeMap(({ payload }) =>
      http.patch(api.settings.selfMonitoring.stormMode, { isEnabled: payload }).pipe(
        mergeMap(() =>
          of(
            actions.fetchStormModeSettingsSuccess({ isEnabled: payload }),
            notificationsActions.enqueue({
              message: 'Storm Mode Settings updated',
              options: { variant: 'success' },
            }),
            appActions.fetchSystemInfoInit()
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchStormModeSettingsFailed(error));
        })
      )
    )
  );

const updateTcpSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateTcpSettingsInit.match),
    mergeMap(({ payload: { action, values } }) =>
      http.post(api.settings.tcp.action(action), { values }).pipe(
        mergeMap(res =>
          of(
            actions.fetchNetworkSettingsInit({ section: 'tcp' }),
            notificationsActions.enqueue({
              message: 'Traffic Management Settings updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchNetworkSettingsFailed({ section: 'tcp', error }));
        })
      )
    )
  );

const updateProtocolSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateProtocolSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.post(api.settings.protocol, values).pipe(
        mergeMap(res =>
          of(
            actions.fetchNetworkSettingsInit({ section: 'protocol' }),
            notificationsActions.enqueue({
              message: 'Protocol Settings updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchNetworkSettingsFailed({ section: 'protocol', error }));
        })
      )
    )
  );

const fetchAlarmHistoryLifeTimeSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAlarmHistoryLifeTimeSettingsInit.match),
    mergeMap(() =>
      http.getJSON<{ days: number }>(api.settings.selfMonitoring.alarmHistoryLifeTime).pipe(
        mergeMap(values => of(actions.fetchAlarmHistoryLifeTimeSettingsSuccess(values))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAlarmHistoryLifeTimeSettingsFailed(error));
        })
      )
    )
  );

const updateAlarmHistoryLifeTimeSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAlarmHistoryLifeTimeSettingsInit.match),
    mergeMap(({ payload }) =>
      http.patch(api.settings.selfMonitoring.alarmHistoryLifeTime, payload).pipe(
        mergeMap(() =>
          of(
            actions.fetchAlarmHistoryLifeTimeSettingsSuccess(payload),
            notificationsActions.enqueue({
              message: 'Alarm History Settings updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAlarmHistoryLifeTimeSettingsFailed(error));
        })
      )
    )
  );

const updateAutoSupervisionSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAutoSupervisionSettingsInit.match),
    mergeMap(({ payload: { supervisionEnable } }) =>
      http.post(api.system.autoSupervision, { supervisionEnable }).pipe(
        mergeMap(res =>
          of(
            actions.updateAutoSupervisionSettingsSuccess(),
            notificationsActions.enqueue({
              message: 'Auto Supervision Settings updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchSystemSettingsFailed({ section: 'auto-supervision', error }));
        })
      )
    )
  );

const fetchHydrantRadiusSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchHydrantRadiusSettingsInit.match),
    mergeMap(() =>
      http.getJSON<HydrantRadiusSettings>(api.settings.selfMonitoring.hydrantRadius).pipe(
        mergeMap(values => of(actions.fetchHydrantRadiusSettingsSuccess({ values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchHydrantRadiusSettingsFailed(error));
        })
      )
    )
  );

const updateHydrantRadiusSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateHydrantRadiusSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.patch(api.settings.selfMonitoring.hydrantRadius, values).pipe(
        mergeMap(() =>
          of(
            actions.fetchHydrantRadiusSettingsSuccess({ values }),
            notificationsActions.enqueue({
              message: 'Hydrant Radius Settings updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchHydrantRadiusSettingsFailed(error));
        })
      )
    )
  );

const fetchNotificationStatusEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchNotificationsStatusInit.match),
    mergeMap(() =>
      http.getJSON<{ sendNotification: boolean }>(api.system.orphanNotifications.status).pipe(
        mergeMap(res => of(
          actions.fetchNotificationsStatusSuccess(res)
        )),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchNotificationsStatusFailed(error));
        })
      )
    )
  );

const fetchAutoAckSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAutoAckSettingsInit.match),
    mergeMap(() =>
      http.getJSON<AutoAckSettings[]>(api.settings.selfMonitoring.autoAck.all).pipe(
        mergeMap(values => of(actions.fetchAutoAckSettingsSuccess({ values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAutoAckSettingsFailed(error));
        })
      )
    )
  );

const updateAutoAckSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAutoAckSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.post(api.settings.selfMonitoring.autoAck.all, values).pipe(
        mergeMap(() =>
          of(
            notificationsActions.enqueue({
              message: 'Auto Acknowledge Settings updated',
              options: { variant: 'success' },
            }),
            actions.fetchAutoAckSettingsInit(),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateAutoAckSettingsFailed(error));
        })
      )
    ));

const deleteAutoAckSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteAutoAckSettingsInit.match),
    mergeMap(({ payload: { id } }) =>
      http.delete(api.settings.selfMonitoring.autoAck.byId(id)).pipe(
        mergeMap(() =>
          of(
            notificationsActions.enqueue({
              message: 'Auto Acknowledge Event deleted',
              options: { variant: 'success' },
            }),
            actions.fetchAutoAckSettingsInit(),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.deleteAutoAckSettingsFailed(error));
        })
      )
    ));

const updateNetworkInstancesEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateNetworkInstancesInit.match),
    mergeMap(({ payload: { values } }) =>
      http.patch(api.settings.instances, values).pipe(
        mergeMap(() =>
          of(
            actions.updateNetworkInstancesSuccess(),
            notificationsActions.enqueue({
              message: 'Network Instances updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateNetworkInstancesFailed(error));
        })
      )
    ));

const fetchAppLogsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAppLogsInit.match),
    mergeMap(() =>
      http.getJSON<INCCLogFiles[]>(api.system.inccLogs.app).pipe(
        mergeMap(values => of(
          actions.fetchAppLogsSuccess({ values }),
        )),
        catchError(() => of(actions.inccLogsFailed())),
      ))
  );

const fetchDbLogsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchDbLogsInit.match),
    mergeMap(() =>
      http.getJSON<INCCLogFiles[]>(api.system.inccLogs.db).pipe(
        mergeMap(values => of(
          actions.fetchDbLogsSuccess({ values }),
        )),
        catchError(() => of(actions.inccLogsFailed())),
      ))
  );

const downloadINCCLogFileEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.downloadINCCLogFileInit.match),
    mergeMap(({ payload: { fileName, isAppLog } }) =>
      http
        .call({
          method: 'GET',
          url: isAppLog ? api.system.inccLogs.downloadApp(fileName) : api.system.inccLogs.downloadDb(fileName),
          responseType: 'blob' as 'json',
        })
        .pipe(
          mergeMap(res => {
            const logFile = fileName === 'all' ?
              (isAppLog ? 'application.all.log.gz' : 'mariadb.all.log.gz') : fileName;
            saveFile(res.response, logFile);

            return of(
              actions.downloadINCCLogSuccess(),
              notificationsActions.enqueue({
                message: `${logFile} has been downloaded`,
                options: { variant: 'success' },
              })
            );
          }),
          catchError(() => of(actions.inccLogsFailed()))
        )
    )
  );

const fetchAutoAckEventSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAutoAckEventSettingsInit.match),
    mergeMap(() =>
      http.getJSON<AutoAckEventTypes>(api.settings.selfMonitoring.autoAck.events).pipe(
        mergeMap(values => of(actions.fetchAutoAckEventSettingsSuccess({ values }))),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchAutoAckEventSettingsFailed(error));
        })
      )
    )
  );

const updateAutoAckEventSettingsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAutoAckEventSettingsInit.match),
    mergeMap(({ payload: { values } }) =>
      http.patch(api.settings.selfMonitoring.autoAck.events, values).pipe(
        mergeMap(() =>
          of(
            notificationsActions.enqueue({
              message: 'Auto Acknowledge Event Types updated',
              options: { variant: 'success' },
            }),
            actions.fetchAutoAckEventSettingsInit(),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.updateAutoAckEventSettingsFailed(error));
        })
      )
    ));

const sendOutboundEmailEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.sendOutboundEmailInit.match),
    mergeMap(({ payload }) =>
      http.getJSON<SMTPOutboundEmailTest>(api.settings.sendOutbound(payload.values)).pipe(
        mergeMap(() =>
          of(
            notificationsActions.enqueue({
              message: 'Outbound Test Email Info updated',
              options: { variant: 'success' },
            }),
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.testEmailConnectionFailed(error));
        })
      )
    )
  );

const testEmailConnectionEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.testEmailConnectionInit.match),
    mergeMap(({ payload }) =>
      http.getJSON<SMTPEmailTestConnection>(api.settings.testOutbound).pipe(
        mergeMap(res => {
          const epicActions: AnyAction[] = [];
          if (!res?.isConnected) {
            epicActions.push(
              notificationsActions.enqueue({
                message: res?.error as string,
                options: { variant: 'error' },
              })
            );
          }
          if (res?.isConnected) {
            epicActions.push(
              notificationsActions.enqueue({
                message: 'SMTP Connected',
                options: { variant: 'success' },
              })
            );
          }
          return of(...epicActions);
        }),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.testEmailConnectionFailed(error));
        })
      )
    )
  );

export const epics = combineEpics(
  fetchSettingsEpic,
  updateSettingsEpic,
  fetchSystemSettingsEpic,
  updateSystemSettingsEpic,
  fetchServerSettingsEpic,
  updateServerSettingsEpic,
  executeSubtoolsJobEpic,
  updateCheckInGracePeriodEpic,
  updateNetworkSettingsEpic,
  updateIntegrationSettingsEpic,
  fetchAntennaSettingsEpic,
  deleteAntennaEpic,
  addAntennaEpic,
  createSSLRequestEpic,
  upLoadSSLFilesEpic,
  importUnitsBulkAddressEpic,
  createDatabaseDumpEpic,
  fetchDatabaseDumpsEpic,
  fetchDatabaseDumpByIdEpic,
  fetchScheduledDBDumpStatusEpic,
  scheduleDBDumpEpic,
  fetchDatabaseDumpReplicationSettingsEpic,
  updateDatabaseDumpReplicationSettingsEpic,
  databaseDumpReplicationStartNowEpic,
  databaseDumpReplicationReverseStartNowEpic,
  fetchDatabaseDumpHistoryEpic,
  fetchWeeklyReportSettingsEpic,
  updateWeeklyReportSettingsEpic,
  fetchWebRelaySettingsEpic,
  updateWebRelaySettingsEpic,
  fetchWebRelayMonitorSettingsEpic,
  updateWebRelayMonitorSettingsEpic,
  fetchAntennaNamesEpic,
  fetchHighContrastSettingsEpic,
  updateHighContrastSettingsEpic,
  updateTcpSettingsEpic,
  updateProtocolSettingsEpic,
  fetchNetworkSettingsEpic,
  fetchStormModeSettingsEpic,
  updateStormModeSettingsEpic,
  fetchAlarmHistoryLifeTimeSettingsEpic,
  updateAlarmHistoryLifeTimeSettingsEpic,
  updateAutoSupervisionSettingsEpic,
  fetchHydrantRadiusSettingsEpic,
  updateHydrantRadiusSettingsEpic,
  fetchNotificationStatusEpic,
  fetchAutoAckSettingsEpic,
  updateAutoAckSettingsEpic,
  deleteAutoAckSettingsEpic,
  updateNetworkInstancesEpic,
  fetchAppLogsEpic,
  fetchDbLogsEpic,
  downloadINCCLogFileEpic,
  fetchAutoAckEventSettingsEpic,
  updateAutoAckEventSettingsEpic,
  sendOutboundEmailEpic,
  testEmailConnectionEpic
);
export const allEpics = {
  fetchSettingsEpic,
  updateSettingsEpic,
  fetchSystemSettingsEpic,
  updateSystemSettingsEpic,
  fetchServerSettingsEpic,
  updateServerSettingsEpic,
  executeSubtoolsJobEpic,
  updateCheckInGracePeriodEpic,
  updateNetworkSettingsEpic,
  updateIntegrationSettingsEpic,
  fetchAntennaSettingsEpic,
  deleteAntennaEpic,
  addAntennaEpic,
  createSSLRequestEpic,
  upLoadSSLFilesEpic,
  importUnitsBulkAddressEpic,
  createDatabaseDumpEpic,
  fetchDatabaseDumpsEpic,
  fetchDatabaseDumpByIdEpic,
  fetchScheduledDBDumpStatusEpic,
  scheduleDBDumpEpic,
  fetchDatabaseDumpReplicationSettingsEpic,
  updateDatabaseDumpReplicationSettingsEpic,
  databaseDumpReplicationStartNowEpic,
  databaseDumpReplicationReverseStartNowEpic,
  fetchDatabaseDumpHistoryEpic,
  fetchWeeklyReportSettingsEpic,
  updateWeeklyReportSettingsEpic,
  fetchWebRelaySettingsEpic,
  updateWebRelaySettingsEpic,
  fetchWebRelayMonitorSettingsEpic,
  updateWebRelayMonitorSettingsEpic,
  fetchAntennaNamesEpic,
  fetchHighContrastSettingsEpic,
  updateHighContrastSettingsEpic,
  updateTcpSettingsEpic,
  updateProtocolSettingsEpic,
  fetchNetworkSettingsEpic,
  fetchStormModeSettingsEpic,
  updateStormModeSettingsEpic,
  fetchAlarmHistoryLifeTimeSettingsEpic,
  updateAlarmHistoryLifeTimeSettingsEpic,
  updateAutoSupervisionSettingsEpic,
  fetchHydrantRadiusSettingsEpic,
  updateHydrantRadiusSettingsEpic,
  fetchNotificationStatusEpic,
  fetchAutoAckSettingsEpic,
  updateAutoAckSettingsEpic,
  deleteAutoAckSettingsEpic,
  updateNetworkInstancesEpic,
  fetchAppLogsEpic,
  fetchDbLogsEpic,
  downloadINCCLogFileEpic,
  fetchAutoAckEventSettingsEpic,
  updateAutoAckEventSettingsEpic,
  sendOutboundEmailEpic,
  testEmailConnectionEpic
};

declare global {
  namespace AES {
    export interface RootState {
      settings: SettingsState;
    }
    export interface Actions {
      settings: typeof actions;
    }

    export interface Selectors {
      settings: typeof selectors;
    }
  }
}
